import React, { useState, useEffect, useCallback } from 'react';
import missionDebugRegistry from '../../services/missionDebugRegistry';
import {
  MissionDebugMap,
  MissionDebugItem,
  MissionDebugStateItem,
} from '../../types/missionDebug';
import styles from './MissionDebugPanel.module.css';

/** Props for the MissionDebugPanel */
interface MissionDebugPanelProps {
  /** Optional callback to request closing the panel */
  onClose?: () => void;
}

/**
 * Renders the debug controls based on the currently active mission's debug map.
 * Subscribes to the global missionDebugRegistry for updates.
 * Changes are buffered locally and applied only on button press.
 */
const MissionDebugPanel: React.FC<MissionDebugPanelProps> = ({ onClose }) => {
  const [activeMap, setActiveMap] = useState<MissionDebugMap | null>(null);
  // State to hold pending changes before applying
  const [pendingChanges, setPendingChanges] = useState<Map<string, any>>(new Map());
  // Refresh key might still be needed for JSON display updates, but less critical now
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    // Subscribe to registry changes
    const handleRegistryUpdate = (newMap: MissionDebugMap | null) => {
      setActiveMap(newMap);
      // Clear pending changes if the underlying map changes (e.g., component switch)
      setPendingChanges(new Map());
      setRefreshKey(prev => prev + 1); // Refresh to clear inputs potentially
    };
    const unsubscribe = missionDebugRegistry.subscribe(handleRegistryUpdate);

    // Clean up subscription on unmount
    return () => unsubscribe();
  }, []); // Only run on mount and unmount

  // Update local pending state instead of calling item.setValue directly
  const handleLocalChange = useCallback((key: string, value: any, type: MissionDebugStateItem['type']) => {
    let processedValue = value;
    // Perform necessary parsing/type checking based on input type
    if (type === 'number') {
        processedValue = parseFloat(value);
        if (isNaN(processedValue)) processedValue = 0; // Default invalid numbers
    } else if (type === 'object') {
        // Store the raw string for textarea, parsing happens on apply
        processedValue = value; // Store string directly
    }
    // For booleans, the value is already correct from handleCheckboxChange

    setPendingChanges(prev => new Map(prev).set(key, processedValue));
  }, []);


  // Specific handler for checkboxes
   const handleCheckboxChange = useCallback((key: string, checked: boolean) => {
       setPendingChanges(prev => new Map(prev).set(key, checked));
   }, []);

  // Apply all pending changes
  const handleApplyChanges = useCallback(() => {
    if (!activeMap) return;

    let refreshNeeded = false;
    let applyErrorOccurred = false; // Flag to prevent closing if errors happen
    pendingChanges.forEach((pendingValue, key) => {
      const item = activeMap.get(key) as MissionDebugStateItem | undefined;
      if (item && item.kind === 'state') {
        try {
          let valueToSet = pendingValue;
           // Parse JSON string just before setting
          if (item.type === 'object' && typeof pendingValue === 'string') {
             valueToSet = JSON.parse(pendingValue);
          }
          item.setValue(valueToSet);
          if (item.type === 'object') refreshNeeded = true; // Flag refresh for object types
        } catch (e) {
            console.error(`[MissionDebugPanel] Error applying change for key "${key}":`, e);
            applyErrorOccurred = true;
            // Optionally: Add UI feedback for the specific field that failed
        }
      }
    });

    // Clear pending changes after attempting to apply
    setPendingChanges(new Map());
    // Force a refresh if object types were updated to reflect changes
    if (refreshNeeded) {
        setRefreshKey(prev => prev + 1);
    }

    // Call the onClose callback if provided and no errors occurred during apply
    if (!applyErrorOccurred) {
       onClose?.();
    }

  }, [activeMap, pendingChanges, onClose]); // Add onClose to dependencies


  const renderControl = (key: string, item: MissionDebugItem) => {
    if (item.kind === 'action') {
      return (
        <button key={key} onClick={item.action} className={styles.actionButton}>
          {item.label}
        </button>
      );
    }

    // item.kind === 'state'
    const hasPendingChange = pendingChanges.has(key);
    // Use pending value if it exists, otherwise use the live value
    const currentValue = hasPendingChange ? pendingChanges.get(key) : item.getValue();

    switch (item.type) {
      case 'boolean':
        // Ensure the value used for 'checked' is boolean
        const checkedValue = typeof currentValue === 'boolean' ? currentValue : !!item.getValue();
        return (
          <div key={key} className={`${styles.controlGroup} ${hasPendingChange ? styles.pendingChange : ''}`}>
            <label htmlFor={key}>{item.label}</label>
            <input
              id={key}
              type="checkbox"
              // Use the boolean representation for the checked state
              checked={checkedValue}
              onChange={(e) => handleCheckboxChange(key, e.target.checked)}
              className={styles.checkboxInput}
            />
          </div>
        );
      case 'number':
        // Display empty string if null/undefined, otherwise the number
        const numberValue = (typeof currentValue === 'number' && !isNaN(currentValue)) ? currentValue : '';
         return (
          <div key={key} className={`${styles.controlGroup} ${hasPendingChange ? styles.pendingChange : ''}`}>
            <label htmlFor={key}>{item.label}</label>
            <input
              id={key}
              type="number"
              // Use the processed number value for display
              value={numberValue}
              onChange={(e) => handleLocalChange(key, e.target.value, item.type)}
              min={item.options?.min}
              max={item.options?.max}
              step={item.options?.step ?? 1}
              className={styles.numberInput}
            />
          </div>
        );
      case 'string':
         return (
          <div key={key} className={`${styles.controlGroup} ${hasPendingChange ? styles.pendingChange : ''}`}>
            <label htmlFor={key}>{item.label}</label>
            <textarea
              id={key}
              rows={1}
              value={currentValue ?? ''}
              onChange={(e) => handleLocalChange(key, e.target.value, item.type)}
              className={styles.textareaInput}
            />
          </div>
        );
      case 'object':
        // Value in textarea should always be a string (either pending string or stringified live value)
        const textValue = hasPendingChange ? currentValue : JSON.stringify(item.getValue(), null, 2);
        return (
          <div key={key} className={`${styles.controlGroup} ${hasPendingChange ? styles.pendingChange : ''}`}>
            <label htmlFor={key}>{item.label} (JSON)</label>
            <textarea
              id={key}
              // Value is the pending string or stringified live object
              value={textValue}
              onChange={(e) => handleLocalChange(key, e.target.value, item.type)}
              rows={4}
              className={styles.textareaInput}
            />
          </div>
        );
      default:
        // Fallback for unknown types: display as read-only stringified object
        return (
           <div key={key} className={styles.controlGroup}>
            <label htmlFor={key}>{item.label} (Unknown Type)</label>
            <textarea
              id={key}
              readOnly
              value={JSON.stringify(item.getValue(), null, 2)}
              rows={3}
              className={styles.textareaInput}
            />
          </div>
        );
    }
  };

  if (!activeMap) { // Handle case where activeMap might become null briefly
      return (
          <div className={styles.panelContainer} data-testid="mission-debug-panel">
              <p className={styles.noControls}>No active mission debug map.</p>
          </div>
      );
  }


  // Check if there are any registered items (state or actions)
  const hasRegisteredItems = activeMap.size > 0;
  // Check if there are any pending changes to apply
  const hasPending = pendingChanges.size > 0;


  return (
    <div className={styles.panelContainer} data-testid="mission-debug-panel" key={refreshKey}>
      <h3 className={styles.panelTitle}>Mission Debug Panel</h3>
      {!hasRegisteredItems ? (
         <p className={styles.noControls}>No debug controls registered for this mission.</p>
      ) : (
        <>
          {Array.from(activeMap.entries()).map(([key, item]) =>
            renderControl(key, item)
          )}
          {/* Only show Apply button if there are pending changes */}
          {hasPending && (
             <button
               onClick={handleApplyChanges}
               className={styles.applyButton}
               disabled={!hasPending} // Disable if no pending changes
             >
               Apply Changes
             </button>
           )}
        </>
       )}
    </div>
  );
};

export default MissionDebugPanel; 